import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../contexts/I18nContext';
import { Lightbulb, Undo, ZoomIn, ZoomOut } from 'lucide-react';

export default function ImageCanvas({ 
  selectedImage, 
  onColorPick, 
  isPickingMode,
  onCursorMove,
  hoveredColor,
  cursorPosition 
}) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Load image onto canvas
  useEffect(() => {
    if (selectedImage && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        ctx.drawImage(img, 0, 0);
        setImageLoaded(true);
        setZoom(1);
      };
      
      img.src = selectedImage.dataUrl;
    }
  }, [selectedImage]);

  // Handle canvas click for color picking
  const handleCanvasClick = (event) => {
    if (!isPickingMode || !imageLoaded) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    const actualX = Math.round(clickX * (canvas.width / rect.width));
    const actualY = Math.round(clickY * (canvas.height / rect.height));
    if (actualX >= 0 && actualX < canvas.width && actualY >= 0 && actualY < canvas.height) {
      onColorPick(canvas, actualX, actualY);
    }
  };

  // Handle mouse move for color preview
  const handleMouseMove = (event) => {
    if (!isPickingMode || !imageLoaded) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const moveX = event.clientX - rect.left;
    const moveY = event.clientY - rect.top;
    const actualX = Math.round(moveX * (canvas.width / rect.width));
    const actualY = Math.round(moveY * (canvas.height / rect.height));
    if (actualX >= 0 && actualX < canvas.width && actualY >= 0 && actualY < canvas.height) {
      onCursorMove(canvas, actualX, actualY, { clientX: moveX, clientY: moveY });
    }
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.5, 5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.5, 0.25));
  const resetZoom = () => {
    setZoom(1);
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
      container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
    }
  };

  // Mouse interactions (reserved for future pan)
  const handleMouseDown = (e) => { e.preventDefault(); };
  const handleMouseMoveForInteraction = (e) => { handleMouseMove(e); };
  const handleMouseUp = () => {};

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isPickingMode) return;
      switch (event.key) {
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          resetZoom();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPickingMode]);

  // Display dimensions
  const getDisplayDimensions = () => {
    if (!imageSize.width || !imageSize.height) return { width: 0, height: 0 };
    const containerWidth = 800;
    const containerHeight = 600;
    const scaleX = containerWidth / imageSize.width;
    const scaleY = containerHeight / imageSize.height;
    const baseScale = Math.min(scaleX, scaleY, 1);
    return {
      width: imageSize.width * baseScale * zoom,
      height: imageSize.height * baseScale * zoom
    };
  };
  const displayDimensions = getDisplayDimensions();

  const canvasStyle = {
    width: `${displayDimensions.width}px`,
    height: `${displayDimensions.height}px`,
    imageRendering: zoom > 2 ? 'pixelated' : 'auto',
    display: 'block',
    margin: '0 auto'
  };

  // Tooltip positioning
  const getTooltipStyle = () => {
    if (!hoveredColor || !imageLoaded || !cursorPosition) return null;
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return null;

    const wrapperRect = wrapper.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const localX = cursorPosition.clientX ?? 0;
    const localY = cursorPosition.clientY ?? 0;

    const anchorX = (canvasRect.left - wrapperRect.left) + localX;
    let top = (canvasRect.top - wrapperRect.top) + localY - 10;

    const tooltipEstimatedWidth = 120;
    const offset = 20;
    let left;
    const wouldOverflowRight = anchorX + offset + tooltipEstimatedWidth > wrapperRect.width;
    const needsFlip = wouldOverflowRight;

    if (needsFlip) {
      left = anchorX - offset;
      left = Math.min(left, wrapperRect.width - 8);
      left = Math.max(left, tooltipEstimatedWidth + 8);
    } else {
      left = anchorX + offset;
      left = Math.max(8, Math.min(left, wrapperRect.width - tooltipEstimatedWidth - 8));
    }

    const tooltipEstimatedHeight = 10;
    top = Math.max(8, Math.min(top, wrapperRect.height - tooltipEstimatedHeight - 8));

    return { left: `${left}px`, top: `${top}px`, transform: needsFlip ? 'translateX(-100%)' : 'none' };
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Zoom Controls */}
      <div className="flex justify-center mb-10 gap-2">
        <button
          onClick={handleZoomOut}
          className="btn btn-outline px-3 py-1 text-sm"
          disabled={zoom <= 0.25}
          aria-label={t('imageCanvas.zoomOut')}
        >
          <ZoomOut />
        </button>
        <div className="flex items-center px-3 py-1 bg-slate-100 rounded-lg text-sm font-medium text-slate-700" aria-live="polite">
          {Math.round(zoom * 100)}%
        </div>
        <button
          onClick={handleZoomIn}
          className="btn btn-outline px-3 py-1 text-sm"
          disabled={zoom >= 5}
          aria-label={t('imageCanvas.zoomIn')}
        >
          <ZoomIn />
        </button>
        <button
          onClick={resetZoom}
          className="btn btn-outline px-3 py-1 text-sm"
          aria-label={t('imageCanvas.reset')}
          title={t('imageCanvas.reset')}
        >
          <Undo className='mr-1'/> {t('imageCanvas.reset')}
        </button>
      </div>

      {/* Hovered color preview */}
      {hoveredColor && imageLoaded && cursorPosition && (
        <div 
          className="absolute z-20 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-lg px-3 py-2 shadow-lg pointer-events-none"
          style={getTooltipStyle() || undefined}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded border border-slate-300" style={{ backgroundColor: hoveredColor.hex }} />
            <span className="text-sm font-mono font-semibold text-slate-700">
              {hoveredColor.hex}
            </span>
          </div>
        </div>
      )}

      {/* Canvas Container */}
      <div 
        ref={scrollContainerRef}
        className="relative overflow-auto  mx-auto"
        style={{ maxWidth: '90vw', maxHeight: '70vh', minHeight: '200px' }}
        aria-label={t('imageCanvas.viewer')}
      >
        <div 
          className="flex items-center justify-center p-4"
          style={{
            minWidth: '100%',
            minHeight: '100%',
            width: displayDimensions.width > 800 ? `${displayDimensions.width + 32}px` : '100%',
            height: displayDimensions.height > 600 ? `${displayDimensions.height + 32}px` : '100%'
          }}
        >
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleMouseMoveForInteraction}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            style={canvasStyle}
            className={`border border-slate-300 rounded ${imageLoaded ? 'color-picker-cursor' : 'cursor-wait'}`}
            aria-label={t('imageCanvas.canvas')}
          />
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-1 text-center text-xs text-slate-500">
        
        <p><Lightbulb className="inline w-4 h-4 mr-1 text-slate-400" />{t('imageCanvas.hint')}</p>
      </div>
    </div>
  );
}
